import React, { createContext, useContext, useEffect, useState } from 'react';

import noop from 'lodash/noop';
import type { DropResult } from 'react-beautiful-dnd';

import { ExecutionStatus, ExecutionStatusColor } from '@qality/common-model';
import {
  DragDropService,
  ExecutionStatusService,
  FlagService,
} from '@qality/common-service';
import { ExecutionStatuses } from '@qality/common-service/src/ExecutionStatusService';

type Loading = 'none' | 'init' | 'create-status';

export type TestCaseExecutionStatusPageContextValue = {
  loading: Loading;
  isEditScreen: boolean;
  changeScreen: () => void;
  statuses: Array<ExecutionStatus>;
  setStatuses: (value: Array<ExecutionStatus>) => void;
  defaultStatus: ExecutionStatus;
  updateDefaultStatus: (value: ExecutionStatus) => void;
  colors: Array<ExecutionStatusColor>;
  updateStatus: (value: ExecutionStatus) => void;
  updateStatusPosition: (value: DropResult) => void;
  createStatus: () => void;
  deleteStatus: (
    status: ExecutionStatus,
    alternativeStatus: ExecutionStatus
  ) => void;
  newStatusId: number | null;
  clearNewStatusId: () => void;
  isMaxNumberOfStatuses: boolean;
};

export const initialContextValue: TestCaseExecutionStatusPageContextValue = {
  loading: 'init',
  isEditScreen: false,
  changeScreen: () => noop,
  statuses: [],
  setStatuses: (value: Array<ExecutionStatus>) => noop(value),
  defaultStatus: null,
  updateDefaultStatus: (value: ExecutionStatus) => noop(value),
  colors: [],
  updateStatus: (value: ExecutionStatus) => noop(value),
  updateStatusPosition: (value: DropResult) => noop(value),
  createStatus: () => undefined,
  deleteStatus: (status: ExecutionStatus, alternativeStatus: ExecutionStatus) =>
    noop(status, alternativeStatus),
  newStatusId: null,
  clearNewStatusId: () => undefined,
  isMaxNumberOfStatuses: false,
};

export const TestCaseExecutionStatusPageContext =
  createContext<TestCaseExecutionStatusPageContextValue>(initialContextValue);

export const useTestCaseExecutionStatusPageContext =
  (): TestCaseExecutionStatusPageContextValue => {
    return useContext(TestCaseExecutionStatusPageContext);
  };

export const MAX_NUMBER_OF_STATUSES = 11;

type Props = {
  children: React.ReactNode;
};

export default function TestCaseExecutionStatusPageContextProvider({
  children,
}: Props): JSX.Element {
  const [loading, setLoading] = useState<Loading>('init');
  const [statuses, setStatuses] = useState<ExecutionStatuses>({
    allStatuses: [],
    defaultStatus: null,
  });
  const [newStatusId, setNewStatusId] = useState<number | null>(null);
  const [isEditScreen, setIsEditScreen] = useState(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    setLoading('init');
    try {
      const newStatuses = await ExecutionStatusService.getStatuses({
        showColors: true,
      });
      setStatuses(newStatuses);
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        "We couldn't get execution statuses. Please try again."
      );
    } finally {
      setLoading('none');
    }
  };

  const changeScreen = () => {
    setIsEditScreen(!isEditScreen);
  };

  const clearNewStatusId = () => {
    setNewStatusId(null);
  };

  const updateStatusWithCorrectPosition = async (status: ExecutionStatus) => {
    const currentPosition =
      1 + statuses.allStatuses.findIndex((s) => s.id === status.id);

    return await ExecutionStatusService.updateStatus({
      ...status,
      position: currentPosition,
    });
  };

  const createStatus = async () => {
    setLoading('create-status');
    try {
      const newStatus = await ExecutionStatusService.createStatus();
      setNewStatusId(newStatus.id);
      setStatuses({
        ...statuses,
        allStatuses: [...statuses.allStatuses, newStatus],
      });
    } catch {
      FlagService.createErrorFlag(
        'Error',
        "We couldn't create new status. Please try again."
      );
    } finally {
      setLoading('none');
    }
  };

  const updateStatus = async (status: ExecutionStatus): Promise<void> => {
    try {
      await updateStatusWithCorrectPosition(status);
      const updatedStatuses = statuses.allStatuses.map((statusArg) =>
        statusArg.id === status.id ? status : statusArg
      );

      setStatuses({
        ...statuses,
        allStatuses: updatedStatuses,
      });
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        'Sorry, something went wrong, and we were unable to make an update.'
      );
    }
  };

  const updateStatusPosition = async (result: DropResult): Promise<void> => {
    try {
      const statusToUpdate = statuses.allStatuses[result.source.index];

      const statusesWithUpdatedPosition = DragDropService.handleDragEnd(
        statuses.allStatuses,
        result
      )?.map((status, index) => ({
        ...status,
        position: index + 1,
      }));

      if (!statusesWithUpdatedPosition) {
        return;
      }

      setStatuses({
        ...statuses,
        allStatuses: statusesWithUpdatedPosition,
      });

      await ExecutionStatusService.updateStatus({
        ...statusToUpdate,
        position: Number(result.destination.index + 1),
      });
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        'Sorry, something went wrong, and we were unable to make an update.'
      );
    }
  };

  const updateDefaultStatus = async (
    status: ExecutionStatus
  ): Promise<void> => {
    try {
      await updateStatusWithCorrectPosition(status);
      const updatedStatuses = statuses.allStatuses.map((statusArg) =>
        statusArg.id === status.id ? status : statusArg
      );
      const updatedStatusesDefaults = updatedStatuses.map((statusArg) => {
        if (status.id !== statusArg.id) {
          return { ...statusArg, isDefault: false };
        } else {
          return statusArg;
        }
      });

      setStatuses({
        ...statuses,
        allStatuses: updatedStatusesDefaults,
        defaultStatus: status,
      });
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        'Sorry, something went wrong, and  we were unable to make an update.'
      );
    }
  };

  const deleteStatus = async (
    status: ExecutionStatus,
    alternativeStatus: ExecutionStatus
  ): Promise<void> => {
    await ExecutionStatusService.deleteStatus(status.id, alternativeStatus.id);

    let updatedStatuses = statuses.allStatuses.filter(
      (arg) => arg.id !== status.id
    );

    if (status.isDefault) {
      updatedStatuses = updatedStatuses.map((statusArg) =>
        statusArg.id === alternativeStatus.id
          ? { ...alternativeStatus, isDefault: true }
          : statusArg
      );

      setStatuses({
        ...statuses,
        allStatuses: updatedStatuses,
        defaultStatus: alternativeStatus,
      });
    } else {
      setStatuses({
        ...statuses,
        allStatuses: updatedStatuses,
      });
    }
  };

  const isMaxNumberOfStatuses =
    statuses.allStatuses.length === MAX_NUMBER_OF_STATUSES;

  return (
    <TestCaseExecutionStatusPageContext.Provider
      value={{
        loading,
        isEditScreen,
        changeScreen,
        statuses: statuses.allStatuses,
        setStatuses: (value: Array<ExecutionStatus>) => {
          setStatuses({
            ...statuses,
            allStatuses: value,
          });
        },
        defaultStatus: statuses.defaultStatus,
        updateDefaultStatus,
        colors: statuses.colors,
        updateStatus,
        updateStatusPosition,
        deleteStatus,
        createStatus,
        newStatusId,
        clearNewStatusId,
        isMaxNumberOfStatuses,
      }}
    >
      {children}
    </TestCaseExecutionStatusPageContext.Provider>
  );
}
